import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { forgetAthleteFlowState } from '../athlete/storage';
import { loadProviderSession, signOut, watchProviderSession } from '../provider/auth';

/**
 * Query-key prefixes that hold data readable only by the signed-in account. Sign-out must cancel
 * and drop every one of them, or a later sign-in reads another person's cached rows.
 */
export const AUTHENTICATED_QUERY_PREFIXES = [
  'provider',
  'coach',
  'account',
  'invitation-payment',
] as const;

/**
 * Whether the session ended because someone asked it to, rather than because it expired or a
 * protected page turned a visitor away.
 *
 * Every protected area sends an unauthenticated visitor to `/login` carrying the page they were
 * trying to reach, so signing in returns them to it. That is right for someone who was bounced,
 * and wrong after a deliberate sign-out: the destination belongs to the account that just left,
 * and honouring it takes the *next* person somewhere they never asked for — an athlete landing on
 * bookings because an administrator had been there.
 *
 * It is a module flag rather than navigation state because the area's own guard re-navigates as
 * the session drops, and whichever redirect renders last would otherwise decide. This does not
 * race: the flag is set before the session ends and read once, when the sign-in screen mounts.
 */
let deliberateSignOut = false;

/** Read the flag and clear it, so it applies to exactly one visit to the sign-in screen. */
export function consumeDeliberateSignOut(): boolean {
  const asked = deliberateSignOut;
  deliberateSignOut = false;
  return asked;
}

/** Sign out and leave for the sign-in screen, discarding where this account had been. */
export function useSignOut(): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    deliberateSignOut = true;
    // A team chosen before sign-in belongs to this visitor, not to whoever signs in next.
    forgetAthleteFlowState();
    void navigate('/login', { replace: true });
    void signOut();
  }, [navigate]);
}

/**
 * Sign out to sign in as someone else, then come back to `returnTo`: a page this account was sent
 * but may not read, such as a progress report emailed to another account (PLA-1896).
 *
 * Unlike {@link useSignOut} the destination is kept, because it is the reason for signing out. A
 * Google or Apple session ends on this browser without the provider's logout, which can only return
 * to `/login` and would lose the page (as for a booking's "Not you?", PLA-1866).
 */
export function useSwitchAccount(): (returnTo: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (returnTo: string) => {
      forgetAthleteFlowState();
      void navigate('/login', { replace: true, state: { returnTo } });
      void signOut({ returnTo });
    },
    [navigate],
  );
}

/**
 * Shared signed-in boundary for every authenticated area (`/manage` bookings, `/coach`
 * workspaces, `/home`). Kept in one place so a new area cannot forget the cache teardown.
 */
export function useAccountSession(): boolean {
  return Boolean(useAccountIdentity());
}

/** Stable across refresh-token rotation, distinct for each sign-in. */
export function useAccountIdentity(): string | undefined {
  const client = useQueryClient();
  const [identity, setIdentity] = useState(() => loadProviderSession()?.sessionId);

  useEffect(() => {
    let previous = loadProviderSession()?.sessionId;
    return watchProviderSession(() => {
      const next = loadProviderSession()?.sessionId;
      if (next !== previous) {
        for (const prefix of AUTHENTICATED_QUERY_PREFIXES) {
          void client.cancelQueries({ queryKey: [prefix] });
          client.removeQueries({ queryKey: [prefix] });
        }
      }
      previous = next;
      setIdentity(next);
    });
  }, [client]);

  return identity;
}

/** Authenticated areas a sign-in may return to. Anything else falls back to the root gate. */
const RETURN_PATH_PATTERN = /^\/(?:manage|coach|home)(?:[/?#]|$)/;

/**
 * Validate a post-sign-in return path. Only in-app authenticated areas are accepted, so a crafted
 * `returnTo` cannot redirect off-site or into a guest flow. With no usable return path the visitor
 * goes to `/`, where the root gate sends each account type to its own home — never straight to
 * bookings and payments.
 */
export function signInReturnPath(value: unknown, fallback = '/'): string {
  return typeof value === 'string' && RETURN_PATH_PATTERN.test(value) ? value : fallback;
}
